import {
  Header,
  SpaceBetween,
  FormField,
  Container,
  Input,
  Textarea,
  Select,
  SelectProps,
  Button,
  Box,
  StatusIndicator,
  TextContent,
  Alert,
} from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';
import { useState, useRef, useCallback } from 'react';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { EventStepProps } from './components/Common';
import { LocationComponent } from './components/LocationComponent';
import { VisibilityComponent } from './components/VisibilityComponent';
import { AciGrantComponent } from './components/AciGrantComponent';
import { CommunityOrganizationComponent } from './components/CommunityOrganizationComponent';
import { ShiftDetailsComponent } from './components/ShiftDetailsComponent';
import { AdvancedSignupComponent } from './components/AdvancedSignupComponent';
import { CoordinatorsPartnersComponent } from './components/CoordinatorsPartnersComponent';
import { ContactsComponent } from './components/ContactsComponent';
import { EventInput, ShiftInput } from '@amzn/red-velvet-api';

export const getEventDetailsStep = (
  title: string,
  description: string,
  currentFields: EventInput,
  updateFieldHandler: (fields: EventInput) => void,
  errors?: Record<string, string>
) => {
  return {
    title: title,
    description: description,
    content: (
      <SpaceBetween direction="vertical" size="l">
        <BasicInfoContent
          currentFields={currentFields}
          updateFieldHandler={updateFieldHandler}
          errors={errors}
        />
        <AdditionalInfoContent
          currentFields={currentFields}
          updateFieldHandler={updateFieldHandler}
          errors={errors}
        />
      </SpaceBetween>
    ),
  };
};

/////////////////////////////////////////////////////////////////////////////
/////////////////// Event Detail Steps Content ///////////////////
/////////////////////////////////////////////////////////////////////////////

const EVENT_TITLE_INPUT_MAX_CHAR = 100;
const EVENT_DESCRIPTION_INPUT_MAX_CHAR = 5000;
const EVENT_SUMMARIZED_DESCRIPTION_INPUT_MAX_CHAR = 150;

function BasicInfoContent(props: EventStepProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);
  const { currentFields, updateFieldHandler, errors, isReadOnly } = props;

  const [eventTitle, setEventTitle] = useState(currentFields.title ?? '');
  const [eventDescription, setEventDescription] = useState(currentFields.description ?? '');
  const [summarizedDescription, setSummarizedDescription] = useState(
    currentFields.shortDescription ?? ''
  );

  const updateTextField = () => {
    if (eventTitle !== '' || eventDescription !== '' || summarizedDescription !== '') {
      updateFieldHandler({
        ...currentFields,
        title: eventTitle,
        description: eventDescription,
        shortDescription: summarizedDescription,
      });
    }
  };

  return (
    <Container header={<Header variant="h2">{t('eventDetailsStep.basicInformation')}</Header>}>
      <SpaceBetween direction="vertical" size="l">
        <FormField
          label={t('eventDetailsStep.eventTitle')}
          description={t('eventDetailsStep.eventTitleSubText')}
          errorText={
            errors?.eventTitle ??
            (eventTitle?.length >= EVENT_TITLE_INPUT_MAX_CHAR
              ? t('error.inputTooLong', { maxLength: EVENT_TITLE_INPUT_MAX_CHAR })
              : '')
          }
        >
          <Input
            placeholder={t('eventDetailsStep.eventTitlePlaceholder')}
            onChange={(event) => {
              setEventTitle(event.detail.value);
            }}
            onBlur={() => updateTextField()}
            value={eventTitle}
            readOnly={isReadOnly}
          />
        </FormField>
        <FormField
          label={t('eventDetailsStep.eventDescription')}
          description={t('eventDetailsStep.eventDescriptionSubText')}
          errorText={
            errors?.eventDescription ??
            (eventDescription?.length >= EVENT_DESCRIPTION_INPUT_MAX_CHAR
              ? t('error.inputTooLong', { maxLength: EVENT_DESCRIPTION_INPUT_MAX_CHAR })
              : '')
          }
        >
          <Textarea
            onChange={(event) => {
              setEventDescription(event.detail.value);
            }}
            onBlur={() => updateTextField()}
            value={eventDescription}
            placeholder={t('eventDetailsStep.eventDescriptionPlaceholder')}
            readOnly={isReadOnly}
          />
        </FormField>
        <FormField
          label={t('eventDetailsStep.summarizedDescription')}
          description={t('eventDetailsStep.summarizedDescriptionSubText')}
          errorText={
            errors?.summarizedDescription ??
            (summarizedDescription?.length >= EVENT_SUMMARIZED_DESCRIPTION_INPUT_MAX_CHAR
              ? t('error.inputTooLong', { maxLength: EVENT_SUMMARIZED_DESCRIPTION_INPUT_MAX_CHAR })
              : '')
          }
        >
          <Textarea
            onChange={(event) => {
              setSummarizedDescription(event.detail.value);
            }}
            onBlur={() => updateTextField()}
            value={summarizedDescription}
            placeholder={t('eventDetailsStep.summarizedDescriptionPlaceholder')}
            readOnly={isReadOnly}
          />
        </FormField>
        <CommunityOrganizationComponent {...props} />
        <LocationComponent {...props} />
        <VisibilityComponent {...props} />
      </SpaceBetween>
    </Container>
  );
}

function AdditionalInfoContent(props: EventStepProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);
  const focusOptions = t('focus', { returnObjects: true }) as SelectProps.Options;
  const primaryActivityOptions = t('activity', { returnObjects: true }) as SelectProps.Options;

  const [focusArea, setFocusArea] = useState<OptionDefinition | null>(
    props.currentFields.focusArea ? { value: props.currentFields.focusArea } : null
  );
  const [primaryActivity, setPrimaryActivity] = useState<OptionDefinition | null>(
    props.currentFields.primaryActivity ? { value: props.currentFields.primaryActivity } : null
  );

  return (
    <Container header={<Header variant="h2">{t('eventDetailsStep.additionalInfo')}</Header>}>
      <SpaceBetween direction="vertical" size="l">
        <FormField
          label={t('eventDetailsStep.focusArea')}
          description={t('eventDetailsStep.focusAreaSubText')}
          errorText={props.errors?.focusArea}
        >
          <Select
            onChange={(event) => {
              setFocusArea(event.detail.selectedOption);
              props.updateFieldHandler({
                ...props.currentFields,
                focusArea: event.detail.selectedOption.value ?? '',
              });
            }}
            selectedOption={focusArea}
            options={focusOptions}
            readOnly={props.isReadOnly}
          />
        </FormField>
        <FormField
          label={t('eventDetailsStep.primaryActivity')}
          description={t('eventDetailsStep.primaryActivitySubText')}
          errorText={props.errors?.primaryActivity}
        >
          <Select
            onChange={(event) => {
              setPrimaryActivity(event.detail.selectedOption);
              props.updateFieldHandler({
                ...props.currentFields,
                primaryActivity: event.detail.selectedOption.value ?? '',
              });
            }}
            selectedOption={primaryActivity}
            options={primaryActivityOptions}
            readOnly={props.isReadOnly}
          />
        </FormField>
        <AciGrantComponent {...props} />
      </SpaceBetween>
    </Container>
  );
}

/////////////////////////////////////////////////////////////////////////////
/////////////////// Shift Details Step Content ///////////////////
/////////////////////////////////////////////////////////////////////////////

export interface ShiftStepProps {
  currentFields: ShiftInput[];
  updateFieldHandler: (fields: ShiftInput[]) => void;
  eventFields: EventInput;
  updateEventFieldHandler: (fields: EventInput) => void;
  errors?: Record<string, string>;
}

export const getShiftStep = (
  title: string,
  description: string,
  currentFields: ShiftInput[],
  updateFieldHandler: (fields: ShiftInput[]) => void,
  eventFields: EventInput,
  updateEventFieldHandler: (fields: EventInput) => void,
  errors?: Record<string, string>
) => {
  return {
    title,
    description,
    content: (
      <ShiftStepContent
        currentFields={currentFields}
        updateFieldHandler={updateFieldHandler}
        eventFields={eventFields}
        updateEventFieldHandler={updateEventFieldHandler}
        errors={errors}
      />
    ),
  };
};

function ShiftStepContent({
  currentFields,
  updateFieldHandler,
  eventFields,
  updateEventFieldHandler,
  errors,
}: ShiftStepProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);
  const [allExpanded, setAllExpanded] = useState(true);
  const [expandToggleKey, setExpandToggleKey] = useState(0);

  const nextId = useRef(currentFields.length);
  const shiftKeys = useRef<number[]>(currentFields.map((_, i) => i));

  const getKey = (index: number): number => {
    while (shiftKeys.current.length <= index) {
      shiftKeys.current.push(nextId.current++);
    }
    return shiftKeys.current[index];
  };

  const updateShift = (index: number, updated: ShiftInput) => {
    const newShifts = [...currentFields];
    newShifts[index] = updated;
    updateFieldHandler(newShifts);
  };

  const removeShift = (index: number) => {
    shiftKeys.current.splice(index, 1);
    const newShifts = currentFields.filter((_, i) => i !== index);
    updateFieldHandler(newShifts);
  };

  const addShift = () => {
    const midnight = new Date(new Date().toDateString());
    updateFieldHandler([
      ...currentFields,
      { startDateTime: midnight, endDateTime: midnight, useEventLocation: true },
    ]);
  };

  const toggleAll = () => {
    setAllExpanded((prev) => !prev);
    setExpandToggleKey((prev) => prev + 1);
  };

  const fieldsRef = useRef(currentFields);
  fieldsRef.current = currentFields;
  const handlerRef = useRef(updateFieldHandler);
  handlerRef.current = updateFieldHandler;

  const eventFieldsRef = useRef(eventFields);
  eventFieldsRef.current = eventFields;
  const eventHandlerRef = useRef(updateEventFieldHandler);
  eventHandlerRef.current = updateEventFieldHandler;

  const stableUpdateEventFields = useCallback((overrides: EventInput) => {
    eventHandlerRef.current({ ...eventFieldsRef.current, ...overrides });
  }, []);

  return (
    <Box margin={{ bottom: 'l' }}>
      <SpaceBetween size="l">
        <Container
          header={
            <Header
              variant="h2"
              actions={
                <Button variant="normal" onClick={toggleAll}>
                  {allExpanded ? t('shiftStep.collapseAll') : t('shiftStep.expandAll')}
                </Button>
              }
            >
              {t('shiftStep.eventShifts')}
            </Header>
          }
        >
          <SpaceBetween size="l">
            {currentFields.map((shift, index) => (
              <ShiftDetailsComponent
                key={getKey(index)}
                shift={shift}
                index={index}
                updateShift={updateShift}
                onRemove={currentFields.length > 1 ? removeShift : undefined}
                errors={errors}
                expandOverride={allExpanded}
                expandToggleKey={expandToggleKey}
              />
            ))}
            <Button iconName="add-plus" variant="link" onClick={addShift}>
              {t('shiftStep.addShift')}
            </Button>
          </SpaceBetween>
        </Container>
        <AdvancedSignupComponent
          eventFields={eventFields}
          updateEventFields={stableUpdateEventFields}
        />
      </SpaceBetween>
    </Box>
  );
}

/////////////////////////////////////////////////////////////////////////////
/////////////////// Team & Partnership Step Content ///////////////////
/////////////////////////////////////////////////////////////////////////////

export const getTeamAndPartnershipStep = (
  title: string,
  description: string,
  currentFields: EventInput,
  updateFieldHandler: (fields: EventInput) => void,
  errors?: Record<string, string>
) => {
  return {
    title,
    description,
    content: (
      <TeamAndPartnershipStepContent
        currentFields={currentFields}
        updateFieldHandler={updateFieldHandler}
        errors={errors}
      />
    ),
    isOptional: false,
  };
};

function TeamAndPartnershipStepContent(props: EventStepProps) {
  return (
    <SpaceBetween size="l">
      <CoordinatorsPartnersComponent {...props} />
      <ContactsComponent {...props} />
    </SpaceBetween>
  );
}

export const getPublishStep = (
  title: string,
  eventId: string,
  previewText: string,
  reviewAndPublishStepProps: ReviewAndPublishStepProps
) => {
  const result = {
    title: title,
    info: (
      <Button data-testid="preview-btn" href={`/view-event?id=${eventId}`}>
        {previewText}
      </Button>
    ),
    content: (
      <SpaceBetween direction="vertical" size="l">
        {ReviewAndPublishContent(reviewAndPublishStepProps)}
      </SpaceBetween>
    ),
  };
  return result;
};

/////////////////////////////////////////////////////////////////////////////
/////////////////// Review And Publish Step Content ///////////////////
/////////////////////////////////////////////////////////////////////////////

interface ReviewAndPublishStepProps {
  currentEventFields: EventInput;
  currentShiftFields?: ShiftInput[];
  updateEventFieldHandler: (fields: EventInput) => void;
  updateShiftFieldHandler?: (fields: ShiftInput[]) => void;
  setActiveStepIndex: (index: number) => void;
  errors?: Record<string, string>;
}

function ReviewAndPublishContent(props: ReviewAndPublishStepProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);
  const { currentEventFields, updateEventFieldHandler, errors, setActiveStepIndex } = props;

  return (
    <Box margin={{ bottom: 'l' }}>
      <SpaceBetween size="xxl">
        {/* Event Details Section */}
        <SpaceBetween size="xs" data-testid="step-1-review">
          <Container>
            <TextContent>
              <b>{t('reviewPublishStep.publication')}</b>
            </TextContent>
            <StatusIndicator type="in-progress">{t('reviewPublishStep.draft')}</StatusIndicator>
          </Container>
          <Header
            variant="h3"
            headingTagOverride="h2"
            actions={
              <Button data-testid="event-edit-step-btn" onClick={() => setActiveStepIndex(0)}>
                {t('misc.edit')}
              </Button>
            }
          >
            {t('eventDetailsStep.eventDetails')}
          </Header>
          <BasicInfoContent
            currentFields={currentEventFields}
            updateFieldHandler={updateEventFieldHandler}
            errors={errors}
            isReadOnly
          />
          <AdditionalInfoContent
            currentFields={currentEventFields}
            updateFieldHandler={updateEventFieldHandler}
            errors={errors}
            isReadOnly
          />
        </SpaceBetween>
        {/* Shift Details Section */}
        <SpaceBetween size="xs" data-testid="step-2-review">
          <Header
            variant="h3"
            headingTagOverride="h2"
            actions={
              <Button data-testid="shift-edit-step-btn" onClick={() => setActiveStepIndex(1)}>
                {t('misc.edit')}
              </Button>
            }
          >
            {t('shiftStep.stepTitle')}
          </Header>
        </SpaceBetween>
        {/* Team & Partnership Details Section */}
        <SpaceBetween size="xs" data-testid="step-3-review">
          <Header
            variant="h3"
            headingTagOverride="h2"
            actions={
              <Button data-testid="team-edit-step-btn" onClick={() => setActiveStepIndex(2)}>
                {t('misc.edit')}
              </Button>
            }
          >
            {t('teamPartnershipStep.stepTitle')}
          </Header>
        </SpaceBetween>
        {props.errors?.publishError !== undefined && (
          <Alert type="error" header={t('error.publishErrorHeader')}>
            {props.errors.publishError}
          </Alert>
        )}
      </SpaceBetween>
    </Box>
  );
}
